import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    Renderer2,
    Self,
    ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { PxHintRowDirective } from '../../directives';
import { FormUtil } from '../../utils';

// Increasing integer for generating unique ids for radio components.
let nextUniqueId = 0;

export class RadioChange {
    constructor(public source: RadioComponent, public value: string | boolean | number) {}
}

const REQUIRED_SIGN = ' *';

@Directive({ selector: '[pxRadioMainLabel]' })
export class RadioMainLabelDirective {
    @HostBinding('attr.id') id: string;

    private requiredMark: HTMLSpanElement;

    constructor(private el: ElementRef) {}

    setRequiredMark() {
        if (!this.requiredMark) {
            this.requiredMark = document.createElement('span');
            this.requiredMark.setAttribute('aria-label', 'Pflichtfeld!');
            this.requiredMark.innerText = REQUIRED_SIGN;
            this.el.nativeElement.appendChild(this.requiredMark);
        }
    }
}

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'px-radio-group',
})
export class RadioGroupDirective implements AfterContentInit, ControlValueAccessor {
    @Input() id: string;

    @Input()
    get name(): string {
        return this.innerName;
    }

    set name(value: string) {
        this.innerName = value;
        this.updateRadioButtonNames();
    }

    @Input()
    get value(): string | boolean | number {
        return this.innerValue;
    }

    set value(newValue: string | boolean | number) {
        if (this.innerValue === newValue) {
            return;
        }

        // Set this before proceeding to ensure no circular loop occurs with selection.
        this.innerValue = newValue;
        this.removeErrorClass();
        this.updateSelectedRadioFromValue();
        this.checkSelectedRadioButton();
    }

    @Input()
    get selected() {
        return this.innerSelected;
    }

    set selected(selected: RadioComponent | null) {
        this.innerSelected = selected;
        this.value = selected ? selected.value : null;
        this.checkSelectedRadioButton();
    }

    /** Whether the radio group is disabled */
    @Input()
    get disabled(): boolean {
        return this.innerDisabled;
    }

    set disabled(value) {
        this.innerDisabled = coerceBooleanProperty(value);
        this.markRadiosForCheck();
    }

    /** Whether the radio group is required */
    @Input()
    get required(): boolean {
        return this.innerRequired;
    }

    set required(value: boolean) {
        this.innerRequired = coerceBooleanProperty(value);
        this.markRadiosForCheck();
    }

    @Input() errorMessage = 'Bitte treffen Sie eine Auswahl.';

    @Input() formControlName: string;
    @Input() formControl: AbstractControl;
    @Input() ariaLabel: string = null;

    /**
     * Event emitted when the group value changes.
     * Change events are only emitted when the value changes due to user interaction with
     * a radio button (the same behavior as `<input type-"radio">`).
     */
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() readonly change: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    @ContentChildren(forwardRef(() => RadioComponent), { descendants: true })
    radios: QueryList<RadioComponent>;

    @ContentChild(forwardRef(() => RadioMainLabelDirective), { static: true })
    mainLabel: RadioMainLabelDirective;

    @ContentChild(forwardRef(() => RadioMainLabelDirective), { read: ElementRef })
    mainLabelEl: ElementRef;

    @ContentChild(forwardRef(() => PxHintRowDirective), { read: ElementRef })
    hintRowElement: ElementRef;

    @HostBinding('class') klass = 'form-group px-radio-button';
    @HostBinding('attr.role') role = 'radiogroup';
    @HostBinding('attr.aria-label') ariaLabelAttribute = this.ariaLabel
        ? this.ariaLabel
        : 'Bitte treffen Sie eine Auswahl.';

    /** Selected value for the radio group. */
    private innerValue: string | boolean | number = null;

    /** The HTML name attribute applied to radio buttons in this group. */
    private innerName = `radio-group-${nextUniqueId++}`;

    /** The currently selected radio button. Should match value. */
    private innerSelected: RadioComponent | null = null;

    /** Whether the `value` has been set to its initial value. */
    private innerIsInitialized = false;

    /** Whether the radio group is disabled. */
    private innerDisabled = false;

    /** Whether the radio group is required. */
    private innerRequired = false;

    private errorComponent: HTMLElement = null;
    private fieldsetElement: HTMLFieldSetElement = null;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private el: ElementRef,
        @Self() private ngControl: NgControl,
        private renderer: Renderer2
    ) {
        if (ngControl) {
            ngControl.valueAccessor = this;
        }
    }

    controlValueAccessorChangeFn: (value: string | boolean | number) => void = () => {};

    onTouched: () => void = () => {};

    checkSelectedRadioButton() {
        if (this.innerSelected && !this.innerSelected.checked) {
            this.innerSelected.checked = true;
        }
    }

    ngAfterContentInit() {
        this.innerIsInitialized = true;
        this.required = this.isRequired();
        if (this.mainLabel) {
            this.mainLabel.id = `${this.id}_txt`;
        }

        if (this.required) {
            this.mainLabel.setRequiredMark();
        }

        // monkey patched because no way to listen to touched event
        const originalFunc = this.ngControl.control.markAsTouched;
        const that = this;
        this.ngControl.control.markAsTouched = function () {
            originalFunc.apply(this, arguments);
            if (that.ngControl.control.invalid) {
                that.setErrorClass();
            }
        };

        const originalResetFunc = this.ngControl.control.reset;
        this.ngControl.control.reset = function () {
            originalResetFunc.apply(this, arguments);
            that.removeErrorClass();
        };

        this.fieldsetElement = this.renderer.createElement('fieldset');
        this.fieldsetElement.className = 'ba-input-group';

        if (this.mainLabelEl) {
            this.fieldsetElement.appendChild(this.mainLabelEl.nativeElement);
        }

        const btnGroup = document.createElement('div');
        btnGroup.setAttribute('role', 'radiogroup');
        btnGroup.className = 'ba-radio-group';

        this.fieldsetElement.appendChild(btnGroup);

        this.radios.forEach((radio) => {
            btnGroup.appendChild(radio.elementRef.nativeElement);
        });

        this.renderer.appendChild(this.el.nativeElement, this.fieldsetElement);

        this.setHintClass();
    }

    isRequired() {
        return FormUtil.hasValidatorKey(this.ngControl.control, 'required');
    }

    getErrorElement() {
        const p = document.createElement('p');
        p.className = 'form-text ba-error';
        p.setAttribute('aria-live', 'polite');
        p.id = `${this.id}_err`;
        p.innerText = this.errorMessage;
        return p;
    }

    setHintClass() {
        if (this.hintRowElement) {
            this.renderer.appendChild(this.fieldsetElement, this.hintRowElement.nativeElement);
        }
    }

    removeHintClass() {
        if (this.hintRowElement && this.fieldsetElement?.contains(this.hintRowElement.nativeElement)) {
            this.renderer.removeChild(this.fieldsetElement, this.hintRowElement.nativeElement);
        }
    }

    setErrorClass() {
        if (this.fieldsetElement) {
            this.renderer.addClass(this.fieldsetElement, 'ba-error');
        }
        if (!this.errorComponent) {
            this.errorComponent = this.getErrorElement();
            this.renderer.appendChild(this.el.nativeElement, this.errorComponent);
            this.removeHintClass();
        }
    }

    removeErrorClass() {
        if (this.fieldsetElement) {
            this.renderer.removeClass(this.fieldsetElement, 'ba-error');
        }
        if (this.errorComponent && this.el.nativeElement?.contains(this.errorComponent)) {
            this.renderer.removeChild(this.el.nativeElement, this.errorComponent);
            this.errorComponent = null;
            this.setHintClass();
        }
    }

    /**
     * Mark this group as being "touched" (for ngModel). Meant to be called by the contained
     * radio buttons upon their blur.
     */
    touch() {
        if (this.onTouched) {
            this.onTouched();
        }
        if (!this.selected) {
            this.setErrorClass();
        }
    }

    /** Dispatch change event with current selection and group value. */
    emitChangeEvent(): void {
        if (this.innerIsInitialized) {
            this.change.emit(new RadioChange(this.innerSelected, this.innerValue));
        }
    }

    markRadiosForCheck() {
        if (this.radios) {
            this.radios.forEach((radio) => radio.markForCheck());
        }
    }

    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value: string) {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     *
     * @param fn Callback to be registered.
     */
    registerOnChange(fn: (value: string) => void) {
        this.controlValueAccessorChangeFn = fn;
    }

    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     *
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     *
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this.changeDetectorRef.markForCheck();
    }

    private updateRadioButtonNames(): void {
        if (this.radios) {
            this.radios.forEach((radio) => {
                radio.name = this.name;
                radio.markForCheck();
            });
        }
    }

    /** Updates the `selected` radio button from the internal innerValue state. */
    private updateSelectedRadioFromValue(): void {
        // If the value already matches the selected radio, do nothing.
        const isAlreadySelected = this.innerSelected !== null && this.innerSelected.value === this.innerValue;

        if (this.radios && !isAlreadySelected) {
            this.innerSelected = null;
            this.radios.forEach((radio) => {
                radio.checked = this.value === radio.value;
                if (radio.checked) {
                    this.innerSelected = radio;
                }
            });
        }
    }
}

@Component({
    selector: 'px-radio-button',
    templateUrl: 'radio.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadioComponent implements OnInit, AfterViewInit, OnDestroy {
    /** The native `<input type=radio>` element */
    @ViewChild('input', { static: true }) innerInputElement: ElementRef<HTMLInputElement>;

    @HostBinding('class') klass = 'form-check';

    /** Whether this radio button is checked. */
    @Input()
    get checked(): boolean {
        return this.innerChecked;
    }

    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this.innerChecked !== newCheckedState) {
            this.innerChecked = newCheckedState;
            if (newCheckedState && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            } else if (!newCheckedState && this.radioGroup && this.radioGroup.value === this.value) {
                // When unchecking the selected radio button, update the selected radio
                // property on the group.
                this.radioGroup.selected = null;
            }

            if (newCheckedState) {
                // Notify all radio buttons with the same name to un-check.
                this.innerRadioDispatcher.notify(this.id, this.name);
            }
            this.innerChangeDetector.markForCheck();
        }
    }

    /** The value of this radio button. */
    @Input()
    get value(): string | boolean | number {
        return this.innerValue;
    }

    set value(value: string | boolean | number) {
        if (this.innerValue === value) {
            return;
        }

        this.innerValue = value;

        if (this.radioGroup === null) {
            return;
        }

        if (!this.checked) {
            // Update checked when the value changed to match the radio group's value
            this.checked = this.radioGroup.value === value;
        }

        if (this.checked) {
            this.radioGroup.selected = this;
        }
    }

    /** Whether the radio button is disabled. */
    @Input()
    get disabled(): boolean {
        return this.innerDisabled || (this.radioGroup !== null && this.radioGroup.disabled);
    }

    set disabled(value: boolean) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this.innerDisabled !== newDisabledState) {
            this.innerDisabled = newDisabledState;
            this.innerChangeDetector.markForCheck();
        }
    }

    /** Whether the radio button is required. */
    @Input()
    get required(): boolean {
        return this.innerRequired || (this.radioGroup && this.radioGroup.required);
    }

    set required(value: boolean) {
        this.innerRequired = coerceBooleanProperty(value);
    }

    get inputId(): string {
        return `${this.id || this.innerUniqueId}_rbt`;
    }

    get labelId(): string {
        return `${this.id || this.innerUniqueId}_lbl`;
    }

    @Input() name: string;
    @Input('aria-label') ariaLabel: string;
    @Input('aria-labelledby') ariaLabelledby: string;
    @Input('aria-describedby') ariaDescribedby: string;

    @Output() readonly radioChangeEventEmitter: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    /** The parent radio group. May or may not be present. */
    radioGroup: RadioGroupDirective;

    private innerUniqueId = `radio-${++nextUniqueId}`;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @Input() id: string = this.innerUniqueId;

    /** Whether this radio is checked. */
    private innerChecked = false;

    /** Whether this radio is disabled. */
    private innerDisabled: boolean;

    /** Whether this radio is required. */
    private innerRequired: boolean;

    /** Value assigned to this radio. */
    private innerValue: string | boolean | number = null;

    constructor(
        @Optional() radioGroup: RadioGroupDirective,
        public elementRef: ElementRef,
        private innerChangeDetector: ChangeDetectorRef,
        private focusMonitor: FocusMonitor,
        private innerRadioDispatcher: UniqueSelectionDispatcher
    ) {
        this.radioGroup = radioGroup;

        this.removeUniqueSelectionListener = innerRadioDispatcher.listen((id: string, name: string) => {
            if (id !== this.id && name === this.name) {
                this.checked = false;
            }
        });
    }

    /** Focuses the radio button. */
    focus(options?: FocusOptions): void {
        this.focusMonitor.focusVia(this.innerInputElement, 'keyboard', options);
    }

    /**
     * Marks the radio button as needing checking for change detection.
     * This method is exposed because the parent radio group will directly
     * update bound properties of the radio button.
     */
    markForCheck() {
        // When group value changes, the button will not be notified. Use `markForCheck` to explicit
        // update radio button's status
        this.innerChangeDetector.markForCheck();
    }

    ngOnInit() {
        if (this.radioGroup) {
            // If the radio is inside a radio group, determine if it should be checked
            this.checked = this.radioGroup.value === this.innerValue;
            // Copy name from parent radio group
            this.name = this.radioGroup.name;
        }
    }

    ngAfterViewInit() {
        this.focusMonitor.monitor(this.elementRef, true).subscribe((focusOrigin) => {
            if (!focusOrigin && this.radioGroup) {
                this.radioGroup.touch();
            }
        });
    }

    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.elementRef);
        this.removeUniqueSelectionListener();
    }

    onInputClick(event: Event) {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `radio-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
    }

    /**
     * Triggered when the radio button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    onInputChange(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();

        const groupValueChanged = this.radioGroup && this.value !== this.radioGroup.value;
        this.checked = true;
        this.emitChangeEvent();

        if (this.radioGroup) {
            this.radioGroup.controlValueAccessorChangeFn(this.value);
            if (groupValueChanged) {
                this.radioGroup.emitChangeEvent();
            }
        }
    }

    /** Unregister function for innerRadioDispatcher */
    private readonly removeUniqueSelectionListener: () => void = () => {};

    /** Dispatch change event with current value. */
    private emitChangeEvent(): void {
        this.radioChangeEventEmitter.emit(new RadioChange(this, this.innerValue));
    }
}
